import React, { useEffect, useState } from "react";
import { api } from "../lib/api";

/**
 * Demo Mode banner + system status context — spec §3.
 * Polls /api/system/status (public, unauthenticated). Renders a persistent
 * top-of-page banner when DEMO_MODE is enabled. Returns the current status
 * via useSystemStatus hook for downstream demo-data badges.
 */

const STATUS_KEY = "auditiq_system_status";

export const useSystemStatus = () => {
  const [status, setStatus] = useState(() => {
    try {
      const cached = localStorage.getItem(STATUS_KEY);
      return cached ? JSON.parse(cached) : null;
    } catch (_) {
      return null;
    }
  });

  useEffect(() => {
    let alive = true;
    const fetch = async () => {
      try {
        const res = await api.get("/system/status");
        if (alive) {
          setStatus(res.data);
          try {
            localStorage.setItem(STATUS_KEY, JSON.stringify(res.data));
          } catch (_) { /* ignore quota */ }
        }
      } catch (e) {
        // Status endpoint is best-effort — banner just won't appear and the
        // 3-tier fallback (spec §1) handles UI. Iter 19h: log via warn for
        // observability instead of silent-catch.
        // eslint-disable-next-line no-console
        if (typeof window !== "undefined" && !window.__auditiqStatusWarned) {
          if (process.env.NODE_ENV !== "production") console.warn("[DemoModeBanner] /api/system/status unreachable:", e?.message || e);
          window.__auditiqStatusWarned = true;
        }
      }
    };
    fetch();
    const id = setInterval(fetch, 60_000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, []);

  return status;
};

export const DemoModeBanner = () => {
  const status = useSystemStatus();
  if (!status?.demo_mode) return null;

  return (
    <div
      data-testid="demo-mode-banner-global"
      style={{
        position: "sticky",
        top: 0,
        zIndex: 9000,
        width: "100%",
        background:
          "linear-gradient(90deg, rgba(13,31,60,0.98), rgba(17,34,68,0.98))",
        borderBottom: "2px solid #c9a84c",
        color: "#f4efe6",
        fontFamily: "Merriweather, Georgia, serif",
        fontSize: 12,
        textAlign: "center",
        padding: "8px 14px",
        letterSpacing: 0.4,
      }}
    >
      <span
        style={{
          color: "#c9a84c",
          fontFamily: "Cinzel, Georgia, serif",
          fontWeight: 600,
          letterSpacing: 1.2,
          marginRight: 8,
          textTransform: "uppercase",
        }}
      >
        Demo Mode
      </span>
      This system demonstrates institutional oversight capabilities for athletic
      programs. All medical and HIPAA-sensitive data is protected and never
      exposed in demo mode.
    </div>
  );
};

/** Inline pill — drop this into any panel that contains seeded demo data. */
export const DemoDataBadge = ({ compact = false }) => (
  <span
    data-testid="demo-data-badge"
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      padding: compact ? "2px 6px" : "3px 9px",
      background: "rgba(201,168,76,0.14)",
      border: "1px solid rgba(201,168,76,0.55)",
      color: "#c9a84c",
      borderRadius: 4,
      fontFamily: "Cinzel, Georgia, serif",
      fontSize: compact ? 9 : 10,
      letterSpacing: 1,
      textTransform: "uppercase",
      fontWeight: 600,
    }}
  >
    Demo Data — Not Real Athlete Records
  </span>
);

export default DemoModeBanner;
